/**
 * Model Switch Eval API — isolated from OrgUsageAnalytics routing.
 *
 * These admin routes are not deployed to Cloud Functions yet. On localhost we
 * talk only to the local webserver (port 8080) so we do not wait on the Firebase
 * emulator (5003) or hit undeployed cloud URLs.
 *
 * Start the backend with the Firestore project that owns your runs:
 *   ENV=production npm run dev:webserver   # prod runs (e.g. PMwHyXBItJmdK0E7Ec7t)
 *   ENV=development npm run dev:webserver    # dev runs
 */
import { getAuth } from "firebase/auth";
import {
  apiUrlDev,
  apiUrlLocal,
  apiUrlProdFirebase,
} from "../../globalVariables";

const BASE = "/ai-pipeline-eval";

const isLocalhost = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

/** Eval-specific base URLs — does not affect other admin pages. */
function getEvalBaseUrls(env) {
  if (isLocalhost()) {
    return [apiUrlLocal];
  }

  return env === "dev" ? [apiUrlDev] : [apiUrlProdFirebase];
}

function getUserId() {
  const userId = getAuth()?.currentUser?.uid;
  if (!userId) {
    throw new Error("Not authenticated. Please refresh and sign in again.");
  }

  return userId;
}

function buildUrl(baseUrl, path, params = {}) {
  const trimmedBase = baseUrl.replace(/\/$/, "");
  const url = new URL(`${trimmedBase}/admin${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
}

async function evalFetch(path, params = {}, { timeoutMs = 60000, env = "prod", method = "GET", body = undefined } = {}) {
  const userId = getUserId();
  const baseUrls = getEvalBaseUrls(env);
  const attempts = [];

  for (const baseUrl of baseUrls) {
    const url = buildUrl(baseUrl, path, { ...params, userId });
    try {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), timeoutMs);
      const fetchOptions = { signal: controller.signal, method };
      if (body !== undefined) {
        fetchOptions.headers = { "Content-Type": "application/json" };
        fetchOptions.body = JSON.stringify(body);
      }
      const res = await fetch(url, fetchOptions);
      window.clearTimeout(timer);
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        const reason = `${res.status} ${res.statusText || ""}`.trim();
        attempts.push({ baseUrl, status: reason, body: text.slice(0, 160) });
        continue;
      }

      return await res.json();
    } catch (err) {
      const msg = err?.message || String(err);
      attempts.push({ baseUrl, status: "network/abort", body: msg });
    }
  }

  const summary = attempts
    .map((a) => `• ${a.baseUrl} → ${a.status}${a.body ? ` (${a.body})` : ""}`)
    .join("\n");
  const hint = isLocalhost()
    ? "\n\nLocal: run `ENV=production npm run dev:webserver` in backend/functions (port 8080)."
    : "";

  throw new Error(
    `Model Switch Eval ${env} request failed:\n${summary || "(no endpoints tried)"}${hint}`,
  );
}

/** List recent comparison runs. */
export const fetchEvalRuns = ({ limit } = {}, env) =>
  evalFetch(BASE, { limit }, { env, timeoutMs: 30000 });

/** Fetch one run's status metadata (and signed report URLs when ready). */
export const fetchEvalRun = (runId, env) =>
  evalFetch(`${BASE}/runs/${runId}`, {}, { env, timeoutMs: 30000 });

/** Full report body — the diff views read this. Longer timeout for large cases. */
export const fetchEvalReport = (runId, env) =>
  evalFetch(`${BASE}/runs/${runId}/report`, {}, { env, timeoutMs: 90000 });

/** Queue a new baseline-vs-candidate comparison. */
export const startEvalRun = (payload, env) =>
  evalFetch(
    `${BASE}/run`,
    {},
    {
      env,
      method: "POST",
      body: payload,
      timeoutMs: 30000,
    },
  );

/** Production baseline + approved candidate catalog for the picker. */
export const fetchEvalModels = (env) =>
  evalFetch(`${BASE}/models`, {}, { env, timeoutMs: 30000 });

/** Re-run a slice of a parent run as a linked child run. */
export const rerunEvalSlice = (runId, payload, env) =>
  evalFetch(
    `${BASE}/runs/${runId}/rerun`,
    {},
    {
      env,
      method: "POST",
      body: payload,
      timeoutMs: 30000,
    },
  );

/** Golden datasets available to run as a batch. */
export const fetchEvalDatasets = (env) =>
  evalFetch(`${BASE}/datasets`, {}, { env, timeoutMs: 30000 });

/** One dataset with its loans. */
export const fetchEvalDataset = (datasetId, env) =>
  evalFetch(`${BASE}/datasets/${datasetId}`, {}, { env, timeoutMs: 30000 });

/**
 * Create a dataset. Pass `seedFromCorpus: true` to pre-fill it from the
 * checked-in golden-loans CSV instead of starting empty.
 */
export const createEvalDataset = (payload, env) =>
  evalFetch(
    `${BASE}/datasets`,
    {},
    { env, method: "POST", body: payload, timeoutMs: 60000 },
  );

export const deleteEvalDataset = (datasetId, env) =>
  evalFetch(
    `${BASE}/datasets/${datasetId}`,
    {},
    { env, method: "DELETE", timeoutMs: 30000 },
  );

export const addEvalDatasetLoan = (datasetId, payload, env) =>
  evalFetch(
    `${BASE}/datasets/${datasetId}/loans`,
    {},
    { env, method: "POST", body: payload, timeoutMs: 30000 },
  );

export const removeEvalDatasetLoan = (datasetId, { organizationId, loanId }, env) =>
  evalFetch(
    `${BASE}/datasets/${datasetId}/loans`,
    { organizationId, loanId },
    { env, method: "DELETE", timeoutMs: 30000 },
  );

/**
 * Copy a production loan into a dev organization so it can be evaluated
 * repeatedly. Cloning copies documents and storage, so this is slow — the
 * backend refuses to run it outside dev/local.
 */
export const cloneLoanToDev = (payload, env) =>
  evalFetch(
    `${BASE}/clone-to-dev`,
    {},
    { env, method: "POST", body: payload, timeoutMs: 300000 },
  );

/**
 * Resolve document IDs → name + preview URL for a loan. Omit documentIds to
 * fetch every visible document and resolve client-side from the map.
 */
export const fetchLoanDocuments = (
  { organizationId, loanId, documentIds = [] },
  env,
) =>
  evalFetch(
    `${BASE}/documents`,
    {
      organizationId,
      loanId,
      documentIds: documentIds.length ? documentIds.join(",") : undefined,
    },
    { env, timeoutMs: 30000 },
  );

// Test hook — not used in production UI.
export const __testables = { getEvalBaseUrls, evalFetch };
